import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users, ShieldCheck, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader, Pill } from "../components/common";

const blank = { name: "", email: "", password: "", bio: "", response_time: "Within 24 hours", availability: "available", is_admin: false };

export default function CoachManagement() {
  const { user } = useAuth();
  const [coaches, setCoaches] = useState(null);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [reassignTo, setReassignTo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/coaches").then(({ data }) => setCoaches(data)).catch(() => setCoaches([]));
  useEffect(() => { load(); }, []);
  if (!coaches) return <Loader />;

  const openNew = () => { setEditId(null); setForm({ ...blank }); };
  const openEdit = (c) => { setEditId(c.id); setForm({ name: c.name, email: c.email, password: "", bio: c.bio || "", response_time: c.response_time || "Within 24 hours", availability: c.availability || "available", is_admin: (c.roles || []).includes("admin") }); };

  const save = async () => {
    if (!form.name || !form.email) return toast.error("Name and email are required.");
    if (!editId && !form.password) return toast.error("Set an initial password.");
    try {
      if (editId) { await api.put(`/admin/coaches/${editId}`, form); toast.success("Coach updated!"); }
      else { await api.post("/admin/coaches", form); toast.success("Coach added!"); }
      setForm(null); setEditId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save coach."); }
  };

  const openDelete = (c) => { setDelTarget(c); setReassignTo(""); };

  const confirmDelete = async () => {
    if (!delTarget) return;
    if (delTarget.customer_count > 0 && !reassignTo) return toast.error("Choose a coach to reassign clients to.");
    setBusy(true);
    try {
      if (delTarget.customer_count > 0) {
        const { data } = await api.post(`/admin/coaches/${delTarget.id}/reassign`, { target_coach_id: reassignTo });
        toast.success(`${data.reassigned} client(s) moved to ${data.target}.`);
      }
      await api.delete(`/admin/coaches/${delTarget.id}`);
      toast.success("Coach deleted.");
      setDelTarget(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete."); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-heading text-5xl">Coaches</h1><p className="text-secondary mt-1">Manage your coaching team.</p></div>
        <button data-testid="coach-new" onClick={openNew} className="px-5 py-2.5 bg-brand text-white rounded-lg font-bold uppercase text-sm tracking-wide flex items-center gap-2 hover:bg-brand-hover"><Plus className="w-4 h-4" /> Add Coach</button>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-6 max-w-4xl">
        {coaches.map((c) => (
          <div key={c.id} className="card-surface rounded-xl p-5" data-testid={`coach-card-${c.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-brand/20 flex items-center justify-center overflow-hidden">
                  {c.picture ? <img src={c.picture} alt="" className="w-full h-full object-cover" /> : <span className="font-heading text-xl text-brand">{c.name[0]}</span>}
                </div>
                <div>
                  <p className="font-semibold">{c.name}</p>
                  <p className="text-xs text-tertiary">{c.email}</p>
                </div>
              </div>
              <div className="flex gap-1">
                <button data-testid={`coach-edit-${c.id}`} onClick={() => openEdit(c)} className="p-2 text-tertiary hover:text-white"><Pencil className="w-4 h-4" /></button>
                {c.id !== user.id && <button data-testid={`coach-delete-${c.id}`} onClick={() => openDelete(c)} className="p-2 text-tertiary hover:text-brand"><Trash2 className="w-4 h-4" /></button>}
              </div>
            </div>
            {c.bio && <p className="text-sm text-secondary mt-3">{c.bio}</p>}
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <Pill tone="default"><span className="flex items-center gap-1"><Users className="w-3 h-3" /> {c.customer_count} clients</span></Pill>
              {(c.roles || []).includes("admin") && <Pill tone="danger"><span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Admin</span></Pill>}
              <Pill tone={c.availability === "available" ? "success" : "warning"}>{c.availability}</Pill>
            </div>
          </div>
        ))}
      </div>

      {form && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-5 z-50" onClick={() => setForm(null)}>
          <div className="card-surface rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading text-3xl">{editId ? "Edit Coach" : "Add Coach"}</h2>
            <div className="space-y-3 mt-4">
              <input data-testid="coach-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
              <input data-testid="coach-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
              <input data-testid="coach-form-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editId ? "New password (leave blank to keep)" : "Initial password"} className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
              <textarea data-testid="coach-form-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Bio" rows={2} className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
              <div className="grid grid-cols-2 gap-2">
                <select value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} className="bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-sm">{["available", "away", "off-hours"].map((x) => <option key={x}>{x}</option>)}</select>
                <input value={form.response_time} onChange={(e) => setForm({ ...form, response_time: e.target.value })} placeholder="Response time" className="bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-brand" />
              </div>
              <label className="flex items-center justify-between cursor-pointer py-1">
                <span className="text-sm text-secondary">Also grant Administrator access</span>
                <input data-testid="coach-form-admin" type="checkbox" checked={form.is_admin} onChange={(e) => setForm({ ...form, is_admin: e.target.checked })} className="accent-[#FF3B30] w-5 h-5" />
              </label>
              <div className="flex gap-2 pt-1">
                <button data-testid="coach-form-save" onClick={save} className="flex-1 py-3 bg-brand text-white font-bold uppercase tracking-wide rounded-lg hover:bg-brand-hover">{editId ? "Save Changes" : "Add Coach"}</button>
                <button onClick={() => setForm(null)} className="px-4 border border-white/20 rounded-lg">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {delTarget && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-5 z-[60]" onClick={() => !busy && setDelTarget(null)}>
          <div className="card-surface rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-full bg-brand/15 flex items-center justify-center"><AlertTriangle className="w-6 h-6 text-brand" /></div>
            <h2 className="font-heading text-3xl mt-3">Delete {delTarget.name}?</h2>
            {delTarget.customer_count > 0 ? (
              <>
                <p className="text-secondary text-sm mt-1">This coach has <strong>{delTarget.customer_count}</strong> assigned client(s). Choose another coach to take them over — clients and conversations will be reassigned before deletion.</p>
                <label className="label-xs block mt-4 mb-2">Reassign clients to</label>
                <select data-testid="reassign-target" value={reassignTo} onChange={(e) => setReassignTo(e.target.value)} className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand">
                  <option value="">Select a coach…</option>
                  {coaches.filter((c) => c.id !== delTarget.id).map((c) => <option key={c.id} value={c.id}>{c.name} ({c.customer_count} clients)</option>)}
                </select>
              </>
            ) : (
              <p className="text-secondary text-sm mt-1">This coach has no assigned clients and will be permanently removed.</p>
            )}
            <div className="flex gap-2 mt-5">
              <button onClick={() => setDelTarget(null)} disabled={busy} className="flex-1 py-2.5 border border-white/20 rounded-lg font-semibold text-sm hover:border-white">Cancel</button>
              <button data-testid="coach-delete-confirm" onClick={confirmDelete} disabled={busy} className="flex-1 py-2.5 bg-brand text-white rounded-lg font-bold text-sm hover:bg-brand-hover disabled:opacity-60">
                {busy ? "Working…" : delTarget.customer_count > 0 ? "Reassign & Delete" : "Delete Coach"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
